import { faCheck } from "@fortawesome/pro-regular-svg-icons";

import { Feature } from "common/types";
import { FetchError } from "common/fetch/types";
import { Optional } from "common/utils/types";
import { SubscriptionPriceSummary } from "common/types/priceSummary";
import { parseNumber } from "common/utils/numbers";
import { toDollars, toPercent } from "common/utils/formatting";
import { toPossessive } from "common/utils/strings";
import Icon from "common/components/Icon";

import ConfirmationDialog from "client/common/components/content/ConfirmationDialog";
import PriceSummary from "client/common/components/display/PriceSummary";
import { useClinic, useFeatures } from "client/clinics/hooks";
import { useClientQuery } from "client/accounts/hooks";

import { DetailSubscription, EligibleRetentionOffer, ListSubscription } from "../../types";
import {
  getRetentionOfferDeadlineFinePrint,
  getRetentionOfferDeliveryDisplay,
  getRetentionOfferDiscountDisplay,
} from "../../utils";

import styles from "../../styles/SubscriptionRetentionOfferStep.module.less";

export type SubscriptionRetentionOfferStepProps = {
  subscription: DetailSubscription | ListSubscription;
  offer: EligibleRetentionOffer;
  onAccept: () => void;
  onDecline: () => void;
  isLoading: boolean;
  isDenyLoading: boolean;
  error: Optional<FetchError>;
};

/**
 * The discount we offer instead of cancelling, priced against the Autoship's next order. Rendered
 * as a step of the cancel dialog, so accepting or declining is the client's only way forward.
 */
const SubscriptionRetentionOfferStep = ({
  subscription,
  offer,
  onAccept,
  onDecline,
  isLoading,
  isDenyLoading,
  error,
}: SubscriptionRetentionOfferStepProps) => {
  const clinic = useClinic();
  const { data: client } = useClientQuery();
  const isEdbEnabled = useFeatures()[Feature.AutoshipEDB];

  const discount = getRetentionOfferDiscountDisplay(offer);
  const discountCap = offer.max_discount
    ? ` (up to ${toDollars(offer.max_discount, { collapseWholeDollars: true })})`
    : "";
  const schedule = subscription.active_frequency.display.toLowerCase();
  const recurringDiscount = clinic.recurring_subscription_discount;
  const autoshipSavings =
    client?.recurring_subscription_eligible_for_clinic && parseNumber(recurringDiscount, 0)
      ? toPercent(recurringDiscount)
      : "";

  const finePrint = getRetentionOfferDeadlineFinePrint(offer, { isEdbEnabled });

  const reassurances = [
    `Applied automatically to ${getRetentionOfferDeliveryDisplay(subscription, { isEdbEnabled })}`,
    autoshipSavings
      ? `Your ${schedule} schedule and ${autoshipSavings} recurring Autoship savings continue`
      : `Your ${schedule} schedule continues`,
    "Cancel or change your Autoship any time",
  ];

  return (
    <ConfirmationDialog
      title={`Save an extra ${discount}${discountCap} on your next order`}
      description={`Keep ${toPossessive(
        subscription.patient.name_display,
      )} Autoship on track and save extra on your next order.`}
      confirmText={`Apply ${discount} off`}
      denyText="Cancel Autoship"
      onConfirm={onAccept}
      onDeny={onDecline}
      isLoading={isLoading}
      isDenyLoading={isDenyLoading}
      error={error}
      classNames={{ title: styles.title }}
    >
      <div className={styles.body}>
        <div className={styles.summary}>
          <PriceSummary.ExternalTax<SubscriptionPriceSummary>
            data={offer.projected_next_order_data}
            tax={null}
            isTaxFinal={false}
            // Keep the client on the offer: no links out to promotion details
            renderPromotionLink={({ label }) => label}
            styles={{
              rows: styles.rows,
              row: styles.row,
              subrows: styles.subrows,
              value: styles.value,
              discount: styles.discount,
              total: styles.total,
            }}
          />
        </div>
        <div className={styles.reassurances}>
          {reassurances.map((reassurance) => (
            <div className={styles.reassurance} key={reassurance}>
              <Icon className={styles.check} icon={faCheck} />
              <span>{reassurance}</span>
            </div>
          ))}
        </div>
        {finePrint && <div className={styles.finePrint}>{finePrint}</div>}
      </div>
    </ConfirmationDialog>
  );
};

export default SubscriptionRetentionOfferStep;
